import { useCallback, useEffect, useState } from "react"
import type { ViraThemeMode } from "./tokens"

export const VIRA_COLOR_MODE_STORAGE_KEY = "vira-color-mode"

export type UseViraColorModeResult = {
  mode: ViraThemeMode
  setMode: (mode: ViraThemeMode) => void
  toggle: () => void
}

function isViraThemeMode(value: unknown): value is ViraThemeMode {
  return value === "light" || value === "dark"
}

function readStoredMode(): ViraThemeMode | null {
  if (typeof window === "undefined") return null
  try {
    const raw = window.localStorage.getItem(VIRA_COLOR_MODE_STORAGE_KEY)
    return isViraThemeMode(raw) ? raw : null
  } catch {
    return null
  }
}

function writeStoredMode(mode: ViraThemeMode): void {
  if (typeof window === "undefined") return
  try {
    window.localStorage.setItem(VIRA_COLOR_MODE_STORAGE_KEY, mode)
  } catch {
    // Ignore quota / private-mode failures — preference is best-effort.
  }
}

/**
 * Parent-owned color mode state for `ViraThemeProvider` + theme controls.
 * Persists to `localStorage` so refresh keeps the last choice.
 *
 * @example
 * ```tsx
 * const { mode, setMode } = useViraColorMode()
 * return (
 *   <ViraThemeProvider mode={mode}>
 *     <ThemeSwitcher mode={mode} onChange={setMode} />
 *     {children}
 *   </ViraThemeProvider>
 * )
 * ```
 */
export function useViraColorMode(
  initial: ViraThemeMode = "light",
): UseViraColorModeResult {
  const [mode, setModeState] = useState<ViraThemeMode>(
    () => readStoredMode() ?? initial,
  )

  useEffect(() => {
    writeStoredMode(mode)
  }, [mode])

  const setMode = useCallback((next: ViraThemeMode) => {
    setModeState(next)
  }, [])

  const toggle = useCallback(() => {
    setModeState((prev) => (prev === "light" ? "dark" : "light"))
  }, [])

  return { mode, setMode, toggle }
}
